import React, { useState, useEffect, useRef } from "react";
import { supabase, ensureLogosBucket } from "./supabase";
import {
  Building2,
  User,
  Mail,
  ArrowRight,
  ArrowLeft,
  Check,
  Upload,
  Image,
  Loader2,
} from "lucide-react";

// Utilidad para atrapar el tab y limitar el foco a los campos del paso actual
function trapTabKey(e, stepRef) {
  if (e.key !== "Tab") return;
  // Solo incluir inputs, selects y textareas visibles y habilitados
  const focusable = Array.from(
    stepRef.current.querySelectorAll(
      "input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled])"
    )
  ).filter((el) => el.offsetParent !== null);
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (e.shiftKey) {
    if (document.activeElement === first) {
      e.preventDefault();
      last.focus();
    }
  } else {
    if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

// Copiado de Arqueos.js
const CustomDropdown = ({ value, onChange, options, placeholder, disabled = false, icon: Icon, style }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedOption, setSelectedOption] = React.useState(null);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    const selected = options.find(opt => opt.value === value);
    setSelectedOption(selected);
  }, [value, options]);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '14px 16px',
          border: '2px solid #f1f5f9',
          borderRadius: '12px',
          background: disabled ? '#f8fafc' : 'white',
          color: disabled ? '#94a3b8' : '#1e293b',
          fontSize: '16px',
          fontWeight: '500',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: disabled ? 'not-allowed' : 'pointer',
          outline: 'none',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          minHeight: '44px',
          touchAction: 'manipulation',
          boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
          ...style,
        }}
        onFocus={(e) => {
          e.target.style.borderColor = '#3b82f6';
          e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
        }}
        onBlur={(e) => {
          e.target.style.borderColor = '#f1f5f9';
          e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.04)';
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
          {Icon && <Icon size={16} color="#64748b" />}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 1000,
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
          marginTop: '4px',
          maxHeight: '200px',
          overflowY: 'auto'
        }}>
          {options.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: 'none',
                background: option.value === value ? '#eff6ff' : 'transparent',
                color: option.value === value ? '#3b82f6' : '#374151',
                fontSize: '16px',
                fontWeight: option.value === value ? '600' : '400',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                touchAction: 'manipulation'
              }}
              onMouseEnter={(e) => {
                if (option.value !== value) {
                  e.target.style.background = '#f8fafc';
                }
              }}
              onMouseLeave={(e) => {
                if (option.value !== value) {
                  e.target.style.background = 'transparent';
                }
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

function RegistroConstructora({ onNavigateToPayment, onNavigateBack }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isIPhone, setIsIPhone] = useState(false);

  // Estados para validación visual
  const [fieldErrors, setFieldErrors] = useState({});

  // Estados del formulario
  const [formData, setFormData] = useState({
    // Datos empresa
    nombre_constructora: "",
    email_constructora: "",
    cuit_constructora: "",
    logo_url: "",

    // Datos admin
    nombre_empleado: "",
    apellido_empleado: "",
    email_empleado: "",
    puesto_empleado: "",
    tipo_empleado: "socio",
    password: "",
  });

  // Estados para el logo
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Estados para la contraseña
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const steps = [
    { id: 1, title: "Datos de la Empresa", icon: Building2 },
    { id: 2, title: "Tu Acceso Personal", icon: User },
    { id: 3, title: "Confirmar y Pagar", icon: Check },
  ];

  const step1Ref = useRef(null);
  const step2Ref = useRef(null);
  const step3Ref = useRef(null);

  useEffect(() => {
    if (typeof document !== "undefined") {
      let metaViewport = document.querySelector('meta[name="viewport"]');
      if (!metaViewport) {
        metaViewport = document.createElement("meta");
        metaViewport.name = "viewport";
        document.head.appendChild(metaViewport);
      }
      metaViewport.content =
        "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
    }

    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setIsMobile(width <= 768);
      // Detectar iPhone 14 Pro (393x852) y similares
      setIsIPhone(width <= 393 && height >= 800);
    };
    handleResize(); // Ejecutar inmediatamente
    window.addEventListener("resize", handleResize);

    // ✅ Verificar bucket de logos al cargar
    ensureLogosBucket();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Función para validar campos y mostrar errores
  const validateStep = (step) => {
    const errors = {};

    if (step === 1) {
      if (!formData.nombre_constructora.trim()) {
        errors.nombre_constructora = true;
      }
      if (!formData.email_constructora.trim()) {
        errors.email_constructora = true;
      }
    }

    if (step === 2) {
      if (!formData.nombre_empleado.trim()) {
        errors.nombre_empleado = true;
      }
      if (!formData.apellido_empleado.trim()) {
        errors.apellido_empleado = true;
      }
      if (!formData.email_empleado.trim()) {
        errors.email_empleado = true;
      }
      if (!formData.password.trim() || formData.password.trim().length < 6) {
        errors.password = true;
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Agregar función para guardar pre-registro en el backend
  async function guardarPreRegistro(formData) {
    try {
      // Detectar entorno y usar la URL correcta
      const isProd = typeof window !== 'undefined' && window.location.hostname === 'nokk.com.ar';
      const url = isProd
        ? 'https://nokk-backend.vercel.app/api/pre-registros'
        : '/api/pre-registros';
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const result = await response.json();
      if (result.success && result.pre_registro_id) {
        return result.pre_registro_id;
      } else {
        throw new Error(result.error || 'Error creando pre-registro');
      }
    } catch (error) {
      throw new Error('Error guardando pre-registro: ' + error.message);
    }
  }

  const handleNext = async () => {
    // Limpiar errores previos
    setFieldErrors({});

    if (currentStep < 3) {
      // Validar paso actual
      if (validateStep(currentStep)) {
        setCurrentStep(currentStep + 1);
      }
    } else {
      // Paso final - subir logo y navegar al pago
      setLoading(true);
      try {
        // Subir logo si existe
        if (logoFile) {
          await handleLogoUpload();
        }
        // Guardar pre-registro en backend y obtener el id
        const preRegistroId = await guardarPreRegistro(formData);
        // Guardar datos en sessionStorage y el pre_registro_id
        sessionStorage.setItem(
          "registro_constructora",
          JSON.stringify({ ...formData, pre_registro_id: preRegistroId })
        );
        // Redirigir a pago (el pre_registro_id se usará en el metadata)
        window.location.href = "/pago";
      } catch (error) {
        console.error("❌ Error en paso final:", error);
        alert("Error procesando registro: " + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBack = () => {
    // Limpiar errores al retroceder
    setFieldErrors({});

    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      onNavigateBack();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Limpiar error del campo cuando el usuario empiece a escribir
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: false,
      }));
    }
  };

  // Funciones para manejo del logo
  const handleLogoSelect = (file) => {
    if (file && file.type.startsWith("image/")) {
      if (file.size <= 5 * 1024 * 1024) {
        // 5MB
        setLogoFile(file);
        const reader = new FileReader();
        reader.onload = (e) => setLogoPreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        alert("El archivo es demasiado grande. Máximo 5MB.");
      }
    } else {
      alert("Por favor selecciona un archivo de imagen válido.");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    handleLogoSelect(file);
  };

  // Funciones para manejo de contraseña
  const handleKeyDown = (e) => {
    if (e.getModifierState("CapsLock")) {
      setCapsLockOn(true);
    }
  };

  const handleKeyUp = (e) => {
    if (!e.getModifierState("CapsLock")) {
      setCapsLockOn(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // ✅ Función para subir logo a Supabase Storage
  const handleLogoUpload = async () => {
    if (!logoFile) return null;

    setUploadingLogo(true);
    try {
      console.log("📤 Iniciando upload del logo...");

      // Generar nombre único para el archivo
      const fileExt = logoFile.name.split(".").pop();
      const fileName = `logos/${Date.now()}-${Math.random()
        .toString(36)
        .substring(2)}.${fileExt}`;

      // Subir archivo a Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("logos")
        .upload(fileName, logoFile, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) {
        console.error("❌ Error subiendo logo:", uploadError);
        throw new Error("Error subiendo logo: " + uploadError.message);
      }

      console.log("✅ Logo subido:", uploadData);

      // Obtener URL pública del archivo
      const { data: urlData } = supabase.storage
        .from("logos")
        .getPublicUrl(fileName);

      const finalUrl = urlData.publicUrl;
      console.log("✅ URL del logo:", finalUrl);

      // Actualizar formulario con la URL del logo
      setFormData((prev) => ({
        ...prev,
        logo_url: finalUrl,
      }));

      return finalUrl;
    } catch (error) {
      console.error("❌ Error completo subiendo logo:", error);
      alert("Error al subir logo: " + error.message);
      return null;
    } finally {
      setUploadingLogo(false);
    }
  };

  // Validaciones de campos obligatorios por paso
  const isStep1Valid =
    formData.nombre_constructora.trim() && formData.email_constructora.trim();
  const isStep2Valid =
    formData.nombre_empleado.trim() &&
    formData.apellido_empleado.trim() &&
    formData.email_empleado.trim() &&
    formData.password.trim().length >= 6;

  const renderStepIndicator = () => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: isIPhone ? "8px" : isMobile ? "12px" : "12px",
      }}
    >
      {steps.map((step, index) => {
        const isActive = step.id === currentStep;
        const isCompleted = step.id < currentStep;
        const IconComponent = step.icon;

        return (
          <React.Fragment key={step.id}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: isMobile ? "6px" : "8px",
              }}
            >
              <div
                style={{
                  width: isIPhone ? "32px" : isMobile ? "40px" : "40px",
                  height: isIPhone ? "32px" : isMobile ? "40px" : "40px",
                  borderRadius: "50%",
                  background: isCompleted
                    ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                    : isActive
                    ? "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                    : "#f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.3s ease",
                  border: isActive
                    ? "2px solid #3b82f6"
                    : "2px solid transparent",
                  boxShadow: isActive
                    ? isMobile
                      ? "0 0 0 3px rgba(59, 130, 246, 0.15), 0 4px 16px rgba(59, 130, 246, 0.25)"
                      : "0 0 0 3px rgba(59, 130, 246, 0.15)"
                    : isCompleted
                    ? isMobile
                      ? "0 4px 16px rgba(16, 185, 129, 0.25)"
                      : "none"
                    : isMobile
                    ? "0 2px 8px rgba(0,0,0,0.08)"
                    : "none",
                  transform: isActive ? "scale(1.05)" : "scale(1)",
                }}
              >
                {isCompleted ? (
                  <Check
                    size={isIPhone ? 16 : isMobile ? 18 : 18}
                    color="white"
                  />
                ) : (
                  <IconComponent
                    size={isIPhone ? 16 : isMobile ? 18 : 18}
                    color={isActive ? "white" : "#64748b"}
                  />
                )}
              </div>
              {!isMobile && (
                <span
                  style={{
                    fontSize: isIPhone ? "10px" : "12px",
                    fontWeight: "600",
                    color: isActive || isCompleted ? "#1e293b" : "#64748b",
                    textAlign: "center",
                    maxWidth: isIPhone ? "80px" : "100px",
                  }}
                >
                  {step.title}
                </span>
              )}
              {isMobile && (
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: isActive ? "700" : "600",
                    color: isActive || isCompleted ? "#1e293b" : "#64748b",
                    textAlign: "center",
                    maxWidth: "70px",
                    lineHeight: "1.2",
                  }}
                >
                  {step.title}
                </span>
              )}
            </div>
            {index < steps.length - 1 && (
              <div
                style={{
                  width: isIPhone ? "12px" : isMobile ? "20px" : "32px",
                  height: "2px",
                  background: isCompleted
                    ? "linear-gradient(90deg, #10b981 0%, #059669 100%)"
                    : "#e2e8f0",
                  transition: "all 0.3s ease",
                  borderRadius: "2px",
                  alignSelf: "center",
                  position: "relative",
                  top: isIPhone ? "-8px" : isMobile ? "-10px" : "-10px",
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  const renderStep1 = () => (
    <form
      ref={step1Ref}
      tabIndex={-1}
      onKeyDown={(e) => trapTabKey(e, step1Ref)}
      autoComplete="off"
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: isIPhone ? "8px" : isMobile ? "16px" : "12px",
          height: "100%",
        }}
      >
        <div>
          <label
            style={{
              display: "block",
              marginBottom: isIPhone ? "4px" : isMobile ? "6px" : "6px",
              fontSize: isIPhone ? "12px" : isMobile ? "13px" : "13px",
              fontWeight: "600",
              color: fieldErrors.nombre_constructora ? "#ef4444" : "#374151",
            }}
          >
            Nombre de la Constructora *
          </label>
          <input
            type="text"
            value={formData.nombre_constructora}
            onChange={(e) =>
              handleInputChange("nombre_constructora", e.target.value)
            }
            placeholder="Constructora ABC S.A."
            required
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            style={{
              width: "100%",
              padding: isIPhone
                ? "8px 10px"
                : isMobile
                ? "14px 12px"
                : "10px 12px",
              border: `2px solid ${
                fieldErrors.nombre_constructora ? "#ef4444" : "#f1f5f9"
              }`,
              borderRadius: isIPhone ? "10px" : isMobile ? "12px" : "10px",
              fontSize: isIPhone ? "12px" : "16px",
              boxSizing: "border-box",
              outline: "none",
              background: fieldErrors.nombre_constructora
                ? "#fef2f2"
                : "#fafafa",
              transition: "all 0.2s ease",
              fontFamily: "inherit",
              boxShadow: isMobile ? "0 2px 8px rgba(0,0,0,0.04)" : "none",
            }}
          />
        </div>

        <div>
          <label
            style={{
              display: "block",
              marginBottom: "6px",
              fontSize: "13px",
              fontWeight: "600",
              color: fieldErrors.email_constructora ? "#ef4444" : "#374151",
            }}
          >
            Email Empresa *
          </label>
          <input
            type="email"
            value={formData.email_constructora}
            onChange={(e) =>
              handleInputChange("email_constructora", e.target.value)
            }
            placeholder="info@constructora.com"
            required
            autoComplete="new-email"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            style={{
              width: "100%",
              padding: isIPhone
                ? "8px 10px"
                : isMobile
                ? "14px 12px"
                : "10px 12px",
              border: `2px solid ${
                fieldErrors.email_constructora ? "#ef4444" : "#f1f5f9"
              }`,
              borderRadius: isIPhone ? "10px" : isMobile ? "12px" : "10px",
              fontSize: isIPhone ? "12px" : "16px",
              boxSizing: "border-box",
              outline: "none",
              background: fieldErrors.email_constructora
                ? "#fef2f2"
                : "#fafafa",
              transition: "all 0.2s ease",
              fontFamily: "inherit",
              boxShadow: isMobile ? "0 2px 8px rgba(0,0,0,0.04)" : "none",
            }}
          />
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isIPhone
              ? "2fr 1fr"
              : isMobile
              ? "2fr 1fr"
              : "1fr",
            gap: isIPhone ? "8px" : isMobile ? "12px" : "12px",
            alignItems: isMobile ? "end" : "start",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "13px",
                fontWeight: "600",
                color: "#374151",
              }}
            >
              CUIT{isMobile ? "" : " (opcional)"}
            </label>
            <input
              type="tel"
              inputMode="numeric"
              pattern="[0-9\-]*"
              value={formData.cuit_constructora}
              onChange={(e) => {
                const numeros = e.target.value.replace(/\D/g, "");
                const limitado = numeros.substring(0, 11);
                let formateado = "";
                if (limitado.length <= 2) {
                  formateado = limitado;
                } else if (limitado.length <= 10) {
                  formateado = `${limitado.substring(
                    0,
                    2
                  )}-${limitado.substring(2)}`;
                } else {
                  formateado = `${limitado.substring(
                    0,
                    2
                  )}-${limitado.substring(2, 10)}-${limitado.substring(10)}`;
                }
                handleInputChange("cuit_constructora", formateado);
              }}
              placeholder="__-________-_"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              style={{
                width: "100%",
                padding: isIPhone
                  ? "8px 10px"
                  : isMobile
                  ? "14px 12px"
                  : "10px 12px",
                border: "2px solid #f1f5f9",
                borderRadius: isIPhone ? "10px" : isMobile ? "12px" : "10px",
                fontSize: isIPhone ? "12px" : "16px",
                boxSizing: "border-box",
                outline: "none",
                background: "#fafafa",
                transition: "all 0.2s ease",
                fontFamily: "monospace",
                boxShadow: isMobile ? "0 2px 8px rgba(0,0,0,0.04)" : "none",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: isIPhone ? "4px" : isMobile ? "6px" : "6px",
                fontSize: isIPhone ? "12px" : isMobile ? "13px" : "13px",
                fontWeight: "600",
                color: "#374151",
              }}
            >
              Logo{isMobile ? "" : " (opcional)"}
            </label>
            <div
              style={{
                border: `2px dashed ${isDragOver ? "#3b82f6" : "#cbd5e1"}`,
                borderRadius: isIPhone ? "10px" : isMobile ? "12px" : "10px",
                padding: isIPhone ? "6px" : isMobile ? "14px 12px" : "12px",
                textAlign: "center",
                background: isDragOver ? "#f0f9ff" : "#fafafa",
                cursor: "pointer",
                transition: "all 0.2s ease",
                position: "relative",
                minHeight: isIPhone ? "42px" : isMobile ? "52px" : "70px",
                display: "flex",
                flexDirection: isMobile ? "row" : "column",
                justifyContent: "center",
                alignItems: "center",
                gap: isMobile ? "8px" : "0",
                boxShadow: isMobile ? "0 2px 8px rgba(0,0,0,0.04)" : "none",
                overflow: "hidden",
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById("logo-input").click()}
            >
              <input
                id="logo-input"
                type="file"
                accept="image/*"
                onChange={(e) => handleLogoSelect(e.target.files[0])}
                style={{ display: "none" }}
              />

              {logoPreview ? (
                <div
                  style={{
                    marginBottom: isIPhone ? "6px" : isMobile ? "8px" : "8px",
                  }}
                >
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    style={{
                      maxWidth: isIPhone ? "80px" : isMobile ? "100px" : "100px",
                      maxHeight: isIPhone ? "50px" : isMobile ? "60px" : "60px",
                      objectFit: "contain",
                      borderRadius: "6px",
                    }}
                  />
                </div>
              ) : (
                <Upload
                  size={isIPhone ? 16 : isMobile ? 20 : 18}
                  color="#64748b"
                  style={{
                    margin: isIPhone
                      ? "0 auto 6px"
                      : isMobile
                      ? "0 auto 8px"
                      : "0 auto 6px",
                  }}
                />
              )}

              <p
                style={{
                  margin: isIPhone
                    ? "0 0 4px 0"
                    : isMobile
                    ? "0 0 4px 0"
                    : "0 0 4px 0",
                  fontSize: isIPhone ? "11px" : isMobile ? "12px" : "12px",
                  color: "#374151",
                }}
              >
                {logoPreview
                  ? "Logo seleccionado"
                  : ""}
              </p>
              {!isMobile && !isIPhone && (
                <p
                  style={{
                    margin: 0,
                    fontSize: isIPhone ? "8px" : isMobile ? "10px" : "9px",
                    color: "#64748b",
                  }}
                >
                  PNG, JPG • Máx 5MB
                </p>
              )}
            </div>
          </div>
        </div>

        {!isMobile && (
          <div>
            <label
              style={{
                display: "block",
                marginBottom: isIPhone ? "4px" : isMobile ? "6px" : "6px",
                fontSize: isIPhone ? "12px" : isMobile ? "13px" : "13px",
                fontWeight: "600",
                color: "#374151",
              }}
            >
              Logo{isMobile ? "" : " (opcional)"}
            </label>
            <div
              style={{
                border: `2px dashed ${isDragOver ? "#3b82f6" : "#cbd5e1"}`,
                borderRadius: isIPhone ? "10px" : isMobile ? "12px" : "10px",
                padding: isIPhone ? "6px" : isMobile ? "14px 12px" : "12px",
                textAlign: "center",
                background: isDragOver ? "#f0f9ff" : "#fafafa",
                cursor: "pointer",
                transition: "all 0.2s ease",
                position: "relative",
                minHeight: isIPhone ? "42px" : isMobile ? "52px" : "70px",
                display: "flex",
                flexDirection: isMobile ? "row" : "column",
                justifyContent: "center",
                alignItems: "center",
                gap: isMobile ? "8px" : "0",
                boxShadow: isMobile ? "0 2px 8px rgba(0,0,0,0.04)" : "none",
                overflow: "hidden",
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById("logo-input").click()}
            >
              <input
                id="logo-input"
                type="file"
                accept="image/*"
                onChange={(e) => handleLogoSelect(e.target.files[0])}
                style={{ display: "none" }}
              />

              {logoPreview ? (
                <div
                  style={{
                    marginBottom: isIPhone ? "6px" : isMobile ? "8px" : "8px",
                  }}
                >
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    style={{
                      maxWidth: isIPhone ? "80px" : isMobile ? "100px" : "100px",
                      maxHeight: isIPhone ? "50px" : isMobile ? "60px" : "60px",
                      objectFit: "contain",
                      borderRadius: "6px",
                    }}
                  />
                </div>
              ) : (
                <Upload
                  size={isIPhone ? 16 : isMobile ? 20 : 18}
                  color="#64748b"
                  style={{
                    margin: isIPhone
                      ? "0 auto 6px"
                      : isMobile
                      ? "0 auto 8px"
                      : "0 auto 6px",
                  }}
                />
              )}

              <p
                style={{
                  margin: isIPhone
                    ? "0 0 4px 0"
                    : isMobile
                    ? "0 0 4px 0"
                    : "0 0 4px 0",
                  fontSize: isIPhone ? "11px" : isMobile ? "12px" : "12px",
                  color: "#374151",
                }}
              >
                {logoPreview
                  ? "Logo seleccionado"
                  : ""}
              </p>
              {!isMobile && !isIPhone && (
                <p
                  style={{
                    margin: 0,
                    fontSize: isIPhone ? "8px" : isMobile ? "10px" : "9px",
                    color: "#64748b",
                  }}
                >
                  PNG, JPG • Máx 5MB
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </form>
  );

  // En renderStep2, aseguro que el input de puesto_empleado sea seguro y el contenedor permita scroll en mobile
  const renderStep2 = () => (
    <form
      ref={step2Ref}
      tabIndex={-1}
      onKeyDown={(e) => trapTabKey(e, step2Ref)}
      autoComplete="off"
      style={{
        overflowY: isMobile ? "auto" : "visible",
        maxHeight: isMobile ? "60vh" : "none",
        minHeight: isMobile ? "320px" : undefined,
        paddingBottom: isMobile ? "16px" : undefined,
        WebkitOverflowScrolling: isMobile ? "touch" : undefined,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: isIPhone ? "8px" : isMobile ? "16px" : "12px",
          height: "100%",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: isIPhone ? "8px" : isMobile ? "12px" : "12px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                marginBottom: isIPhone ? "4px" : isMobile ? "6px" : "6px",
                fontSize: isIPhone ? "12px" : isMobile ? "13px" : "13px",
                fontWeight: "600",
                color: fieldErrors.nombre_empleado ? "#ef4444" : "#374151",
              }}
            >
              Tu Nombre *
            </label>
            <input
              type="text"
              value={formData.nombre_empleado}
              onChange={(e) =>
                handleInputChange("nombre_empleado", e.target.value)
              }
              placeholder="Juan"
              required
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              style={{
                width: "100%",
                padding: isIPhone
                  ? "8px 10px"
                  : isMobile
                  ? "14px 12px"
                  : "10px 12px",
                border: `2px solid ${
                  fieldErrors.nombre_empleado ? "#ef4444" : "#f1f5f9"
                }`,
                borderRadius: isIPhone ? "10px" : isMobile ? "12px" : "10px",
                fontSize: isIPhone ? "12px" : "16px",
                boxSizing: "border-box",
                outline: "none",
                background: fieldErrors.nombre_empleado ? "#fef2f2" : "#fafafa",
                transition: "all 0.2s ease",
                fontFamily: "inherit",
                boxShadow: isMobile ? "0 2px 8px rgba(0,0,0,0.04)" : "none",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: isIPhone ? "4px" : isMobile ? "6px" : "6px",
                fontSize: isIPhone ? "12px" : isMobile ? "13px" : "13px",
                fontWeight: "600",
                color: fieldErrors.apellido_empleado ? "#ef4444" : "#374151",
              }}
            >
              Tu Apellido *
            </label>
            <input
              type="text"
              value={formData.apellido_empleado}
              onChange={(e) =>
                handleInputChange("apellido_empleado", e.target.value)
              }
              placeholder="Pérez"
              required
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              style={{
                width: "100%",
                padding: isIPhone
                  ? "8px 10px"
                  : isMobile
                  ? "14px 12px"
                  : "10px 12px",
                border: `2px solid ${
                  fieldErrors.apellido_empleado ? "#ef4444" : "#f1f5f9"
                }`,
                borderRadius: isIPhone ? "10px" : isMobile ? "12px" : "10px",
                fontSize: isIPhone ? "12px" : "16px",
                boxSizing: "border-box",
                outline: "none",
                background: fieldErrors.apellido_empleado
                  ? "#fef2f2"
                  : "#fafafa",
                transition: "all 0.2s ease",
                fontFamily: "inherit",
                boxShadow: isMobile ? "0 2px 8px rgba(0,0,0,0.04)" : "none",
              }}
            />
          </div>
        </div>

        <div>
          <label
            style={{
              display: "block",
              marginBottom: isIPhone ? "4px" : isMobile ? "6px" : "6px",
              fontSize: isIPhone ? "12px" : isMobile ? "13px" : "13px",
              fontWeight: "600",
              color: fieldErrors.email_empleado ? "#ef4444" : "#374151",
            }}
          >
            Tu Email Personal *
          </label>
                      <input
              type="email"
              value={formData.email_empleado}
              onChange={(e) =>
                handleInputChange("email_empleado", e.target.value)
              }
              placeholder="tu@email.com"
              required
              autoComplete="new-email"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              style={{
                width: "100%",
                padding: isIPhone
                  ? "8px 10px"
                  : isMobile
                  ? "14px 12px"
                  : "10px 12px",
                border: `2px solid ${
                  fieldErrors.email_empleado ? "#ef4444" : "#f1f5f9"
                }`,
                borderRadius: isIPhone ? "10px" : isMobile ? "12px" : "10px",
                fontSize: isIPhone ? "12px" : "16px",
                boxSizing: "border-box",
                outline: "none",
                background: fieldErrors.email_empleado ? "#fef2f2" : "#fafafa",
                transition: "all 0.2s ease",
                fontFamily: "inherit",
                boxShadow: isMobile ? "0 2px 8px rgba(0,0,0,0.04)" : "none",
              }}
            />
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isIPhone
              ? "1fr 120px"
              : isMobile
              ? "1fr 140px"
              : "1fr 1fr",
            gap: isIPhone ? "8px" : isMobile ? "12px" : "12px",
            alignItems: isMobile ? "end" : "start",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                marginBottom: isIPhone ? "4px" : isMobile ? "6px" : "6px",
                fontSize: isIPhone ? "12px" : isMobile ? "13px" : "13px",
                fontWeight: "600",
                color: "#374151",
              }}
            >
              Tu Puesto
            </label>
            <input
              type="text"
              value={formData.puesto_empleado}
              onChange={(e) => handleInputChange("puesto_empleado", e.target.value)}
              placeholder="Director General"
              autoComplete="off"
              autoCorrect="off"
              style={{
                width: "100%",
                padding: isIPhone
                  ? "8px 10px"
                  : isMobile
                  ? "14px 12px"
                  : "10px 12px",
                border: "2px solid #f1f5f9",
                borderRadius: isIPhone ? "10px" : isMobile ? "12px" : "10px",
                fontSize: isIPhone ? "12px" : "16px",
                boxSizing: "border-box",
                outline: "none",
                background: "#fafafa",
                transition: "all 0.2s ease",
                fontFamily: "inherit",
                boxShadow: isMobile ? "0 2px 8px rgba(0,0,0,0.04)" : "none",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: isIPhone ? "4px" : isMobile ? "6px" : "6px",
                fontSize: isIPhone ? "12px" : isMobile ? "13px" : "13px",
                fontWeight: "600",
                color: "#374151",
              }}
            >
              Tipo
            </label>
            <CustomDropdown
              value={formData.tipo_empleado}
              onChange={(val) => handleInputChange("tipo_empleado", val)}
              options={[
                { value: "socio", label: "Socio" },
                { value: "empleado", label: "Empleado" },
              ]}
              placeholder="Tipo"
              icon={User}
              // Ajuste visual para igualar al input
              style={{
                width: "100%",
                padding: isIPhone
                  ? "8px 6px"
                  : isMobile
                  ? "14px 8px"
                  : "10px 8px",
                border: "2px solid #f1f5f9",
                borderRadius: isIPhone ? "10px" : isMobile ? "12px" : "10px",
                fontSize: isIPhone ? "11px" : isMobile ? "13px" : "12px",
                background: "#fafafa",
                boxSizing: "border-box",
                outline: "none",
                transition: "all 0.2s ease",
                fontFamily: "inherit",
                boxShadow: isMobile ? "0 2px 8px rgba(0,0,0,0.04)" : "none",
                height: "auto",
                minHeight: undefined,
              }}
            />
          </div>
        </div>

        <div>
          <label
            style={{
              display: "block",
              marginBottom: isIPhone ? "4px" : isMobile ? "6px" : "6px",
              fontSize: isIPhone ? "12px" : isMobile ? "13px" : "13px",
              fontWeight: "600",
              color: fieldErrors.password ? "#ef4444" : "#374151",
            }}
          >
            Tu Contraseña *
          </label>
          <div style={{ position: "relative" }}>
            <input
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              onKeyDown={handleKeyDown}
              onKeyUp={handleKeyUp}
              placeholder="Mínimo 6 caracteres"
              required
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              style={{
                width: "100%",
                padding: isIPhone
                  ? "8px 40px 8px 10px"
                  : isMobile
                  ? "14px 50px 14px 12px"
                  : "10px 40px 10px 12px",
                border: `2px solid ${
                  fieldErrors.password ? "#ef4444" : "#f1f5f9"
                }`,
                borderRadius: isIPhone ? "10px" : isMobile ? "12px" : "10px",
                fontSize: isIPhone ? "12px" : "16px",
                boxSizing: "border-box",
                outline: "none",
                background: fieldErrors.password ? "#fef2f2" : "#fafafa",
                transition: "all 0.2s ease",
                fontFamily: "inherit",
                boxShadow: isMobile ? "0 2px 8px rgba(0,0,0,0.04)" : "none",
              }}
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              style={{
                position: "absolute",
                right: isIPhone ? "8px" : isMobile ? "12px" : "8px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: isIPhone ? "4px" : isMobile ? "8px" : "4px",
                color: "#64748b",
              }}
            >
              {showPassword ? (
                <svg
                  width={isIPhone ? "12" : isMobile ? "20" : "16"}
                  height={isIPhone ? "12" : isMobile ? "20" : "16"}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg
                  width={isIPhone ? "12" : isMobile ? "20" : "16"}
                  height={isIPhone ? "12" : isMobile ? "20" : "16"}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
            {capsLockOn && (
              <div
                style={{
                  position: "absolute",
                  top: isIPhone ? "-20px" : isMobile ? "-28px" : "-20px",
                  right: "0",
                  fontSize: isIPhone ? "10px" : isMobile ? "12px" : "10px",
                  color: "#ef4444",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <svg
                  width={isIPhone ? "12" : isMobile ? "14" : "12"}
                  height={isIPhone ? "12" : isMobile ? "14" : "12"}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <circle cx="12" cy="16" r="1" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                CAPS LOCK
              </div>
            )}
          </div>
          {fieldErrors.password && (
            null
          )}
        </div>
      </div>
    </form>
  );

  const renderStep3 = () => (
    <form
      ref={step3Ref}
      tabIndex={-1}
      onKeyDown={(e) => trapTabKey(e, step3Ref)}
      autoComplete="off"
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: isIPhone ? "8px" : isMobile ? "16px" : "12px",
          height: "100%",
        }}
      >
        {/* Resumen consolidado */}
        <div
          style={{
            background: "white",
            padding: isIPhone ? "12px" : isMobile ? "18px 14px" : "16px",
            borderRadius: isIPhone ? "10px" : isMobile ? "14px" : "10px",
            border: "1px solid #e2e8f0",
            boxShadow: isIPhone
              ? "0 1px 3px rgba(0, 0, 0, 0.1)"
              : isMobile
              ? "0 4px 16px rgba(0, 0, 0, 0.08)"
              : "0 1px 3px rgba(0, 0, 0, 0.1)",
          }}
        >
          <h4
            style={{
              fontSize: isIPhone ? "13px" : isMobile ? "15px" : "14px",
              fontWeight: "600",
              color: "#1e293b",
              margin: isIPhone
                ? "0 0 12px 0"
                : isMobile
                ? "0 0 14px 0"
                : "0 0 12px 0",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <Building2
              size={isIPhone ? 16 : isMobile ? 18 : 16}
              color="#3b82f6"
            />
            Resumen de tu cuenta
          </h4>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isIPhone
                ? "1fr"
                : isMobile
                ? "1fr"
                : "1fr 1fr",
              gap: isIPhone ? "8px" : isMobile ? "14px" : "12px",
              fontSize: isIPhone ? "11px" : isMobile ? "13px" : "12px",
            }}
          >
            <div>
              <p
                style={{
                  margin: "0 0 4px 0",
                  color: "#64748b",
                  fontWeight: "500",
                }}
              >
                Empresa
              </p>
              <p
                style={{
                  margin: "0 0 2px 0",
                  color: "#1e293b",
                  fontWeight: "600",
                }}
              >
                {formData.nombre_constructora || "Sin especificar"}
              </p>
              <p
                style={{
                  margin: "0 0 2px 0",
                  color: "#64748b",
                }}
              >
                {formData.email_constructora || "Sin especificar"}
              </p>
              {formData.cuit_constructora && (
                <p
                  style={{
                    margin: 0,
                    color: "#64748b",
                  }}
                >
                  CUIT: {formData.cuit_constructora}
                </p>
              )}
            </div>

            <div>
              <p
                style={{
                  margin: "0 0 4px 0",
                  color: "#64748b",
                  fontWeight: "500",
                }}
              >
                Administrador
              </p>
              <p
                style={{
                  margin: "0 0 2px 0",
                  color: "#1e293b",
                  fontWeight: "600",
                }}
              >
                {formData.nombre_empleado} {formData.apellido_empleado}
              </p>
              <p
                style={{
                  margin: "0 0 2px 0",
                  color: "#64748b",
                }}
              >
                {formData.email_empleado}
              </p>
              {formData.puesto_empleado && (
                <p
                  style={{
                    margin: 0,
                    color: "#64748b",
                  }}
                >
                  {formData.puesto_empleado} ({formData.tipo_empleado})
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Plan y precio */}
        <div
          style={{
            background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
            padding: isIPhone ? "12px" : isMobile ? "18px 14px" : "16px",
            borderRadius: isIPhone ? "10px" : isMobile ? "14px" : "10px",
            border: "1px solid #e2e8f0",
            boxShadow: isMobile ? "0 4px 16px rgba(0, 0, 0, 0.08)" : "none",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: isIPhone ? "8px" : isMobile ? "14px" : "12px",
            }}
          >
            <div>
              <h4
                style={{
                  margin: "0 0 2px 0",
                  fontSize: isIPhone ? "13px" : isMobile ? "16px" : "14px",
                  fontWeight: "600",
                  color: "#1e293b",
                }}
              >
                Plan Profesional
              </h4>
              <p
                style={{
                  margin: 0,
                  fontSize: isIPhone ? "11px" : isMobile ? "13px" : "12px",
                  color: "#64748b",
                }}
              >
                Acceso completo
              </p>
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontSize: isIPhone ? "16px" : isMobile ? "22px" : "18px",
                  fontWeight: "700",
                  color: "#059669",
                }}
              >
                $10,000
              </div>
              <div
                style={{
                  fontSize: isIPhone ? "10px" : isMobile ? "11px" : "11px",
                  color: "#64748b",
                }}
              >
                ARS/mes
              </div>
            </div>
          </div>

          <div
            style={{
              fontSize: isIPhone ? "10px" : isMobile ? "12px" : "11px",
              color: "#64748b",
              lineHeight: "1.4",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                marginBottom: "3px",
              }}
            >
              <svg
                width={isIPhone ? "10" : isMobile ? "11" : "10"}
                height={isIPhone ? "10" : isMobile ? "11" : "10"}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="20,6 9,17 4,12" />
              </svg>
              Usuarios ilimitados
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                marginBottom: "3px",
              }}
            >
              <svg
                width={isIPhone ? "10" : isMobile ? "11" : "10"}
                height={isIPhone ? "10" : isMobile ? "11" : "10"}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="20,6 9,17 4,12" />
              </svg>
              Proyectos ilimitados
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                marginBottom: "3px",
              }}
            >
              <svg
                width={isIPhone ? "10" : isMobile ? "11" : "10"}
                height={isIPhone ? "10" : isMobile ? "11" : "10"}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="20,6 9,17 4,12" />
              </svg>
              Soporte técnico incluido
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <svg
                width={isIPhone ? "10" : isMobile ? "11" : "10"}
                height={isIPhone ? "10" : isMobile ? "11" : "10"}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="20,6 9,17 4,12" />
              </svg>
              Precio especial BETA
            </div>
          </div>
        </div>
      </div>
    </form>
  );

  return (
    <>
      {/* Estilos CSS */}
      <style jsx global>{`
        input,
        select,
        textarea,
        button {
          font-size: 16px !important;
          -webkit-text-size-adjust: none;
          -webkit-appearance: none;
          -webkit-border-radius: 0;
          touch-action: manipulation;
        }

        @supports (-webkit-touch-callout: none) {
          .registro-constructora-container {
            min-height: -webkit-fill-available;
            padding-top: env(safe-area-inset-top);
            padding-bottom: env(safe-area-inset-bottom);
          }
        }

        body {
          -webkit-text-size-adjust: 100%;
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          user-select: none;
          touch-action: manipulation;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .fade-in-up {
          animation: fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .nav-button-back:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
          border-color: #cbd5e1;
        }

        .nav-button-back:active {
          transform: translateY(0);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .nav-button-next:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
        }

        .nav-button-next:active {
          transform: translateY(0);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
        }

        .nav-button-next:disabled:hover {
          transform: none;
          box-shadow: 0 4px 12px rgba(148, 163, 184, 0.3);
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }

        .nav-button-next:not(:disabled) {
          animation: pulse 2s infinite;
        }

        .nav-button-next:not(:disabled):hover {
          animation: none;
        }

        @keyframes finalizeGlow {
          0%,
          100% {
            text-shadow: 0 0 5px rgba(255, 255, 255, 0.5);
          }
          50% {
            text-shadow: 0 0 15px rgba(255, 255, 255, 0.8),
              0 0 25px rgba(255, 255, 255, 0.4);
          }
        }

        .finalize-text {
          animation: finalizeGlow 2s ease-in-out infinite;
        }

        /* Mejoras móviles adicionales */
        @media (max-width: 768px) {
          .nav-button-back,
          .nav-button-next {
            -webkit-tap-highlight-color: transparent;
            -webkit-touch-callout: none;
          }

          .nav-button-back:active,
          .nav-button-next:active {
            transform: scale(0.98);
          }
        }
      `}</style>

      {/* Container principal */}
      <div
        className="registro-constructora-container"
        style={{
          height: "100vh",
          height: "100dvh",
          background: isMobile
            ? "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)"
            : "linear-gradient(135deg, #fafbfc 0%, #f1f5f9 25%, #e2e8f0 50%, #cbd5e1 75%, #94a3b8 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isMobile ? "20px 16px" : "20px 24px",
          position: "relative",
          overflow: "hidden",
          fontFamily: '"Inter", sans-serif',
        }}
      >
        {/* Efectos de fondo */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: isMobile
              ? `
                radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.05) 0%, transparent 50%),
                radial-gradient(circle at 70% 80%, rgba(16, 185, 129, 0.05) 0%, transparent 50%)
              `
              : `
                radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.05) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(16, 185, 129, 0.05) 0%, transparent 50%),
                radial-gradient(circle at 40% 40%, rgba(139, 92, 246, 0.05) 0%, transparent 50%)
              `,
            pointerEvents: "none",
          }}
        />

        {/* Card principal */}
        <div
          className="fade-in-up"
          style={{
            background: isMobile
              ? "rgba(255, 255, 255, 0.98)"
              : "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            borderRadius: "24px",
            padding: isIPhone
              ? "16px 12px"
              : isMobile
              ? "24px 20px 20px 20px"
              : "24px 24px",
            width: "100%",
            maxWidth: isMobile ? "400px" : "500px",
            height: isIPhone
              ? "calc(100vh - 16px)"
              : isMobile
              ? "auto"
              : "520px",
            minHeight: isMobile ? "520px" : "520px",
            maxHeight: isMobile ? "85vh" : "520px",
            boxShadow: isMobile
              ? "0 20px 60px -12px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.1)"
              : "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)",
            position: "relative",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              textAlign: "center",
              marginBottom: isIPhone ? "8px" : isMobile ? "20px" : "12px",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                marginBottom: isIPhone ? "4px" : isMobile ? "10px" : "4px",
              }}
            >
              <img
                src="https://llohruvkcdcyolmwlivx.supabase.co/storage/v1/object/public/public-assets//logonokk.png"
                alt="NOKK"
                style={{
                  height: isIPhone ? "20px" : isMobile ? "28px" : "24px",
                  width: "auto",
                  opacity: 0.9,
                }}
              />
            </div>

            <p
              style={{
                fontSize: isIPhone ? "10px" : isMobile ? "13px" : "11px",
                color: "#64748b",
                margin: isIPhone ? "0 0 8px 0" : isMobile ? "0" : "0 0 8px 0",
                fontWeight: "400",
              }}
            >
              Registrá tu constructora en 3 simples pasos
            </p>
          </div>

          {/* Step indicator */}
          <div
            style={{
              flexShrink: 0,
              marginBottom: isIPhone ? "8px" : isMobile ? "20px" : "12px",
              paddingBottom: isMobile ? "16px" : "0",
              borderBottom: isMobile ? "1px solid #f1f5f9" : "none",
            }}
          >
            {renderStepIndicator()}
          </div>

          {/* Step content container con scroll mejorado */}
          <div
            style={{
              flex: 1,
              overflow: "hidden",
              position: "relative",
              marginBottom: isIPhone ? "20px" : isMobile ? "24px" : "12px",
            }}
          >
            <div
              style={{
                display: "flex",
                width: "300%",
                transform: `translateX(-${(currentStep - 1) * 33.333}%)`,
                transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                height: "100%",
              }}
            >
              <div
                style={{
                  width: "33.333%",
                  // Ajuste: padding simétrico solo en mobile
                  paddingLeft: isMobile ? "0" : isIPhone ? "0" : "12px",
                  paddingRight: isMobile ? "0" : isIPhone ? "0" : "12px",
                  marginLeft: isMobile ? "0" : undefined,
                  marginRight: isMobile ? "0" : undefined,
                  overflow: isMobile ? "auto" : "hidden",
                  maxHeight: "100%",
                  paddingBottom: isMobile ? "10px" : "0",
                }}
              >
                <div
                  style={{
                    paddingLeft: isMobile ? "0" : undefined,
                    paddingRight: isMobile ? "0" : undefined,
                  }}
                >
                  {renderStep1()}
                </div>
              </div>
              <div
                style={{
                  width: "33.333%",
                  paddingLeft: isMobile ? "0" : isIPhone ? "0" : "12px",
                  paddingRight: isMobile ? "0" : isIPhone ? "0" : "12px",
                  marginLeft: isMobile ? "0" : undefined,
                  marginRight: isMobile ? "0" : undefined,
                  overflow: isMobile ? "auto" : "hidden",
                  maxHeight: "100%",
                  paddingBottom: isMobile ? "10px" : "0",
                }}
              >
                <div
                  style={{
                    paddingLeft: isMobile ? "0" : undefined,
                    paddingRight: isMobile ? "0" : undefined,
                  }}
                >
                  {renderStep2()}
                </div>
              </div>
              <div
                style={{
                  width: "33.333%",
                  paddingLeft: isMobile ? "0" : isIPhone ? "0" : "12px",
                  paddingRight: isMobile ? "0" : isIPhone ? "0" : "12px",
                  marginLeft: isMobile ? "0" : undefined,
                  marginRight: isMobile ? "0" : undefined,
                  overflow: isMobile ? "auto" : "hidden",
                  maxHeight: "100%",
                  paddingBottom: isMobile ? "10px" : "0",
                }}
              >
                <div
                  style={{
                    paddingLeft: isMobile ? "0" : undefined,
                    paddingRight: isMobile ? "0" : undefined,
                  }}
                >
                  {renderStep3()}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation buttons */}
          <div
            style={{
              display: "flex",
              gap: isIPhone ? "8px" : isMobile ? "14px" : "12px",
              justifyContent: "space-between",
              flexShrink: 0,
              paddingTop: isMobile ? "12px" : "0",
              paddingBottom: isMobile ? "16px" : "0",
              borderTop: isMobile ? "1px solid #f1f5f9" : "none",
              height: isMobile ? "52px" : "auto",
              alignItems: "center",
            }}
          >
            <button
              onClick={handleBack}
              className="nav-button-back"
              style={{
                flex: "1 1 0%",
                padding: isIPhone
                  ? "12px 16px"
                  : isMobile
                  ? "0 20px"
                  : "14px 20px",
                background: "transparent",
                border: "1px solid #d4d4d4",
                borderRadius: isIPhone ? "10px" : isMobile ? "12px" : "10px",
                color: "#374151",
                fontSize: isIPhone ? "12px" : isMobile ? "14px" : "13px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: isIPhone ? "6px" : "8px",
                position: "relative",
                overflow: "hidden",
                boxShadow: "none",
                height: isMobile ? "52px" : "auto",
                minWidth: "0",
                maxWidth: "none",
              }}
            >
              <ArrowLeft size={isIPhone ? 16 : isMobile ? 16 : 16} />
              {currentStep === 1
                ? isMobile
                  ? "Anterior"
                  : "Anterior"
                : "Anterior"}
            </button>

            <button
              onClick={handleNext}
              disabled={loading}
              className="nav-button-next"
              style={{
                flex: "1 1 0%",
                padding: isIPhone
                  ? "12px 16px"
                  : isMobile
                  ? "0 20px"
                  : "14px 20px",
                background: loading
                  ? "#94a3b8"
                  : "#1a1a1a",
                color: "white",
                border: "none",
                borderRadius: isIPhone ? "10px" : isMobile ? "12px" : "10px",
                fontSize: isIPhone ? "12px" : isMobile ? "14px" : "13px",
                fontWeight: "500",
                cursor: loading ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: isIPhone ? "6px" : "8px",
                position: "relative",
                overflow: "hidden",
                boxShadow: "none",
                height: isMobile ? "52px" : "auto",
                minWidth: "0",
                maxWidth: "none",
              }}
            >
              {loading && (
                <Loader2
                  size={isIPhone ? 16 : isMobile ? 16 : 16}
                  className="animate-spin"
                />
              )}
              <span className={currentStep === 3 ? "finalize-text" : ""}>
                {currentStep === 3
                  ? isMobile
                    ? "Finalizar"
                    : "Finalizar registro"
                  : "Siguiente"}
              </span>
              {!loading && (
                <ArrowRight size={isIPhone ? 16 : isMobile ? 16 : 16} />
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default RegistroConstructora;
